import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { EChartsOption } from 'echarts';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import * as echarts from 'echarts';
import { generateUUIDV4 } from 'src/app/helpers/util';
import { SankeyLink } from 'src/app/objects/sankeyTypes';

@Component({
  selector: 'app-generic-echart',
  templateUrl: './generic-echart.component.html',
  styleUrls: ['./generic-echart.component.scss'],
})
export class GenericEchartComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef<HTMLDivElement>;
  chartInstance: echarts.ECharts | null = null;
  @Input() chartOption: EChartsOption;
  @Input() chartOption$: Observable<EChartsOption>;
  @Input() chartLoading$: Observable<boolean>;
  private chartOptionSub?: Subscription;
  uniqueId: string;
  chartId$: BehaviorSubject<string> = new BehaviorSubject(null);
  private outMap = {};
  private inMap = {};
  private edges: SankeyLink[] = [];

  constructor() { }

  ngOnInit() {
    this.uniqueId = generateUUIDV4();
    this.chartId$.next('echarts-' + this.uniqueId);
    // this.chartOptionBtnId$.next('option-btn-' + this.uniqueId);
  }

  ngAfterViewInit() {
    this.initializeChart();
    if (this.chartInstance && this.chartOption) {
      this.chartInstance.setOption(this.chartOption);
      // this.getEdges(this.chartOption.series[0].links as SankeyLink[]);
      // this.hookEventSankey(this.chartInstance);
    }
    if (this.chartOption$) {
      this.chartOptionSub = this.chartOption$.subscribe(chartOption => {
        if (this.chartInstance) {
          if (chartOption) {
            this.chartInstance.setOption(chartOption);
          } else if (this.chartOption) {
            this.chartInstance.setOption(this.chartOption);
          }
          // this.getEdges(chartOption.series?.[0]?.links as SankeyLink[]);
          // this.hookEventSankey(this.chartInstance);
        }
      });
    }
    if (this.chartLoading$) {
      this.chartLoading$.subscribe(loading => {
        if (this.chartInstance) {
          if (loading) {
            this.chartInstance.showLoading({
              text: '',
              textColor: '#fff',
              fontSize: 14,
              fontWeight: 'bold',
              maskColor: 'rgba(33, 33, 33, 0.8)',
              spinnerRadius: 12.5,
              lineWidth: 7,
            });
          } else {
            this.chartInstance.hideLoading();
          }
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.chartInstance && changes.chartOption && changes.chartOption.currentValue) {
      this.chartInstance.setOption(changes.chartOption.currentValue);
    }
  }

  ngOnDestroy() {
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
    if (this.chartOptionSub) {
      this.chartOptionSub.unsubscribe();
    }
  }

  initializeChart() {
    const chartDom = document.getElementById('echarts-' + this.uniqueId)!;
    if (chartDom) {
      if (!this.chartInstance || this.chartInstance.isDisposed()) {
        this.chartInstance = echarts.init(chartDom, null, { renderer: 'svg' });
        // this.cdr.detectChanges();
      }
      const resizeObserver = new ResizeObserver(() => {
        if (!this.chartInstance.isDisposed()) {
          this.chartInstance.resize();
          // this.cdr.detectChanges();
        }
      });

      resizeObserver.observe(chartDom);
      // this.chartResizeObserver = resizeObserver;
      // this.cdr.detectChanges();
    }
  }

  // A BFS to collect all upstream/downstream edge & node indices
  private collectPath(idx: number, direction: string) {
    const edgeQueue = [idx];
    const seenEdges = new Set();
    const seenNodes = new Set();
    while (edgeQueue.length) {
      const eIdx = edgeQueue.pop();
      if (seenEdges.has(eIdx)) continue;
      seenEdges.add(eIdx);
      const e = this.edges[eIdx];
      const nextNode = direction === 'up' ? e.source : e.target;
      if (!seenNodes.has(nextNode)) {
        seenNodes.add(nextNode);
        const nextEdges = (direction === 'up' ? this.inMap[nextNode] : this.outMap[nextNode]) || [];
        nextEdges.forEach(ne => edgeQueue.push(ne));
      }
    }
    return { edgeIndices: [...seenEdges], nodeNames: [...seenNodes] };
  }

  private getEdges(edges: SankeyLink[]) {
    this.edges = edges;
    this.edges.forEach((e, i) => {
      this.outMap[e.source] = this.outMap[e.source] || [];
      this.outMap[e.source].push(i);
      this.inMap[e.target] = this.inMap[e.target] || [];
      this.inMap[e.target].push(i);
    });
  }

  private hookEventSankey(chart: echarts.ECharts) {
    let edgesToH: any[], nodesToH: any[];

    // 3. Hook into chart hover events
    chart.on('mouseover', { seriesType: 'sankey' }, params => {
      // clear any previous highlights
      chart.dispatchAction({ type: 'downplay', seriesIndex: 0 });

      // 4b) compute trace depending on hover target
      if (params.dataType === 'edge') {
        const up = this.collectPath(params.dataIndex, 'up');
        const down = this.collectPath(params.dataIndex, 'down');
        edgesToH = Array.from(new Set([...up.edgeIndices, ...down.edgeIndices]));
        nodesToH = Array.from(new Set([...up.nodeNames, ...down.nodeNames]));
      }
      else if (params.dataType === 'node') {
        const down = this.collectPath(
          // find any outgoing edge to seed, or skip if leaf
          (this.outMap[params.name] || [])[0] ?? params.dataIndex,
          'down'
        );
        edgesToH = down.edgeIndices;
        nodesToH = down.nodeNames.concat(params.name);
      }
      // 4c) highlight just those edges + nodes
      edgesToH.forEach(ei => chart.dispatchAction({
        type: 'highlight',
        seriesIndex: 0,
        dataType: 'edge',
        dataIndex: ei
      }));
      nodesToH.forEach(n => chart.dispatchAction({
        type: 'highlight',
        seriesIndex: 0,
        dataType: 'node',
        name: n
      }));
    });

    // 4. Reset on mouseout
    chart.on('mouseout', { seriesType: 'sankey' }, () => {
      edgesToH.forEach(ei => chart.dispatchAction({
        type: 'downplay',
        seriesIndex: 0,
        dataType: 'edge',
        dataIndex: ei
      }));
      nodesToH.forEach(n => chart.dispatchAction({
        type: 'downplay',
        seriesIndex: 0,
        dataType: 'node',
        name: n
      }));
      chart.dispatchAction({ type: 'downplay', seriesIndex: 0 });
    });
  }

}
